import { createReducer, createAction } from '@reduxjs/toolkit';
import type { AnyAction } from '@reduxjs/toolkit';
import { backendCall, createApiCall, eventCall } from '@reducers/utils';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import type { AppDispatch } from '@reducers/utils/types';
import { workerType, workerTypeStatusNames } from '@helpers';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { CONNECTOR_LOAD_ERROR } from './constants';
import type { ConnectorState, ConnectorDetails, UpdateStatusesPayload } from './types';
import type { GetConnectorHealthResponse } from '@api/domain/types';
import type { OnboardProductionSourceSystemRequestDto } from '@api/generated/models';
import type { ErpSourceSystemTypesParams } from '@api/modules/membership/types';

// Re-export types for consumers
export type { ConnectorState, ConnectorDetails, UpdateStatusesPayload } from './types';

const initState: ConnectorState = {
  loaded: false,
  connectorDetails: {} as ConnectorDetails,
  oldStatuses: undefined,
  errMsg: null,
  isPRODUCTIONExist: false,
};

export const connectorInfoSuccess = createAction<ConnectorDetails>('connector/connectorInfoSuccess');
export const saveId = createAction<string>('connector/saveId');
export const goToOffline = createAction('connector/goToOffline');
export const goToOnboarding = createAction('connector/goToOnboarding');
export const goToResetting = createAction('connector/goToResetting');
export const saveOldStatuses = createAction('connector/saveOldStatuses');
export const restoreStatuses = createAction('connector/restoreStatuses');
export const connectorInfoErrMsg = createAction<string>('connector/connectorInfoErrMsg');
export const updateStatuses = createAction<UpdateStatusesPayload>('connector/updateStatuses');
export const clear = createAction('clear');

let abortControllerConnectorInfo: AbortController | null = null;

export const stopConnectorInfo = (): void => {
  if (abortControllerConnectorInfo) {
    abortControllerConnectorInfo.abort();
  }
};

export const getConnectorInfo = () => async (dispatch: AppDispatch) => {
  stopConnectorInfo();
  abortControllerConnectorInfo = new AbortController();
  const signal = abortControllerConnectorInfo.signal;

  return await dispatch(
    backendCall(
      createApiCall((api) => api.membership.getConnectorHealth, {
        signal,
        onSuccess: (data: GetConnectorHealthResponse) => {
          const isPRODUCTIONExist = Boolean(data?.status?.find((t) => t.workerType === workerType.PRODUCTION));
          dispatch(connectorInfoSuccess({ ...data, isPRODUCTIONExist }));
        },
        onError: (error: unknown) => {
          // Flip `connector.loaded` to true even on failure so the Machines screen can exit
          // for a freshly added account whose tenant has no connector YET (getConnectorHealth 404).
          // Raw error goes to Datadog, not Redux — anything in state leaks into RUM snapshots.
          dispatch(connectorInfoErrMsg(CONNECTOR_LOAD_ERROR));
          dispatch(sendLog({ title: LOGGER_TITLES.CONNECTOR_HEALTH_FAILED, data: { error: String(error) }, status: 'error' }) as unknown as AnyAction);
        },
      })
    )
  );
};

export const erpSourceSystemTypes = (params: ErpSourceSystemTypesParams) => async (dispatch: AppDispatch) => {
  return await dispatch(backendCall(createApiCall((api) => api.membership.erpSourceSystemTypes, { params })));
};

export const onboardProductionSourceSystem =
  (data: OnboardProductionSourceSystemRequestDto, onSuccess?: () => void, onError?: (error: unknown) => void) => async (dispatch: AppDispatch) => {
    const call = createApiCall((api) => api.membership.onboardProductionSourceSystem, {
      params: data,
      onSuccess,
      onError,
      ignoreDefaultErrorHandler: true,
    });

    try {
      const resp = await dispatch(
        eventCall({
          connectionName: 'appHub',
          eventName: 'OnConfiguredProductionSourceSystem',
          eventNameError: 'OnFailedToOnboardProductionSourceSystem',
          data: call,
          timeout: 120,
        })
      );

      trackEvent(TRACKING_EVENTS.CONNECTOR, {
        action: 'added',
        connectorType: ((data as Record<string, unknown>)?.type as string) || ((data as Record<string, unknown>)?.connectorType as string) || '',
        machineId: String((data as Record<string, unknown>)?.machineId || ''),
        machineName: ((data as Record<string, unknown>)?.machineName as string) || '',
      });
      return resp;
    } catch (e) {
      trackEvent(TRACKING_EVENTS.CONNECTOR, {
        action: 'added',
        connectorType: ((data as Record<string, unknown>)?.type as string) || ((data as Record<string, unknown>)?.connectorType as string) || '',
        machineId: String((data as Record<string, unknown>)?.machineId || ''),
        machineName: ((data as Record<string, unknown>)?.machineName as string) || '',
      });
      console.log(e, 'e');
      return e;
    }
  };

export const onChangedConnectorHealth = (e: GetConnectorHealthResponse) => async (dispatch: AppDispatch) => {
  if (e) {
    const isPRODUCTIONExist = Boolean(e?.status?.find((t) => t.workerType === workerType.PRODUCTION));
    dispatch(connectorInfoSuccess({ ...e, isPRODUCTIONExist }));
  }
};

export const onResetedConnector = (_e: unknown) => async (dispatch: AppDispatch) => {
  dispatch(getConnectorInfo());
};

export default createReducer<ConnectorState>(initState, {
  [connectorInfoSuccess.type]: (state, action: ReturnType<typeof connectorInfoSuccess>) => ({
    ...state,
    connectorDetails: action.payload,
    loaded: true,
  }),
  [updateStatuses.type]: (state, action: ReturnType<typeof updateStatuses>) => ({
    ...state,
    connectorDetails: {
      ...state?.connectorDetails,
      ...action.payload.info,
      status: action.payload.statuses,
    },
  }),
  [saveId.type]: (state, action: ReturnType<typeof saveId>) => ({
    ...state,
    connectorDetails: {
      ...state?.connectorDetails,
      connectorId: action.payload,
    },
  }),
  [goToOffline.type]: (state) => ({
    ...state,
    connectorDetails: {
      ...state?.connectorDetails,
      connectorId: null,
      status: state?.connectorDetails?.status?.map((i) => ({ ...i, status: workerTypeStatusNames.Offline })),
    },
  }),
  [goToOnboarding.type]: (state) => ({
    ...state,
    connectorDetails: {
      ...state?.connectorDetails,
      status: state?.connectorDetails?.status?.map((i) => ({ ...i, status: workerTypeStatusNames.Onboarding })),
    },
  }),
  [goToResetting.type]: (state) => ({
    ...state,
    connectorDetails: {
      ...state?.connectorDetails,
      status: state?.connectorDetails?.status?.map((i) => ({ ...i, status: workerTypeStatusNames.Resetting })),
    },
  }),
  [saveOldStatuses.type]: (state) => ({
    ...state,
    oldStatuses: state?.connectorDetails?.status,
  }),
  [restoreStatuses.type]: (state) => ({
    ...state,
    connectorDetails: {
      ...state?.connectorDetails,
      status: state?.oldStatuses,
    },
  }),
  [connectorInfoErrMsg.type]: (state, action: ReturnType<typeof connectorInfoErrMsg>) => ({
    ...state,
    errMsg: action.payload,
    loaded: true,
  }),
  [clear.type]: () => {
    return initState;
  },
});
